// src/core/Tube.ts
import { Segment } from "./types";

export class Tube {
  private _stack: Segment[];
  readonly capacity: number;

  constructor(initial: (Segment | null)[], capacity: number) {
    this.capacity = capacity;
    this._stack = initial
      .filter((s): s is Segment => s !== null)
      .reverse();
    if (this._stack.length > capacity) {
      throw new Error(`Too many segments`);
    }
  }

  /** Public view bottom→top */
  get segments(): (Segment | null)[] {
    const empties = Array(this.capacity - this._stack.length).fill(null);
    return [...this._stack.map(s => ({ color: s.color, hidden: s.hidden })), ...empties];
  }

  /** Pop exactly one segment (or return null if empty) */
  public popSegment(): Segment | null {
    return this._stack.pop() ?? null;
  }

  /** Push exactly one segment (returns false if full) */
  public pushSegment(seg: Segment): boolean {
    if (this._stack.length >= this.capacity) return false;
    this._stack.push(seg);
    return true;
  }

  /** Reveal contiguous hidden segments of the same color at the top */
  public revealTop(): void {
    let idx = this._stack.length - 1;
    if (idx < 0) return;
    const color = this._stack[idx].color;
    while (idx >= 0 && this._stack[idx].hidden && this._stack[idx].color === color) {
      this._stack[idx].hidden = false;
      idx--;
    }
  }

  /** Count how many contiguous top segments share the top color */
  private countTopSameColor(): number {
    const top = this.topColor();
    if (top === null) return 0;
    let count = 0;
    for (let i = this._stack.length - 1; i >= 0; i--) {
      if (this._stack[i].color === top) count++;
      else break;
    }
    return count;
  }

  /** Top color or null */
  public topColor(): string | null {
    return this._stack.length
      ? this._stack[this._stack.length - 1].color
      : null;
  }

  /** How many empty slots remain */
  public emptySlots(): number {
    return this.capacity - this._stack.length;
  }

  /**
   * Pour up to all contiguous top-same-color segments into `other`.
   * Uses popSegment + pushSegment under the hood.
   */
  public pourInto(other: Tube): number {
    // guard conditions
    if (this._stack.length === 0 || other.emptySlots() === 0) return 0;
    const myTop = this.topColor()!;
    const theirTop = other.topColor();
    if (theirTop !== null && theirTop !== myTop) return 0;

    // determine how many to move
    const countTop = this.countTopSameColor();
    const toMove   = Math.min(countTop, other.emptySlots());
    if (toMove === 0) return 0;

    // perform raw moves
    let moved = 0;
    for (let i = 0; i < toMove; i++) {
      const seg = this.popSegment()!;
      if (!other.pushSegment(seg)) break;
      moved++;
    }

    // reveal any newly‐exposed tops
    this.revealTop();
    other.revealTop();
    return moved;
  }

  /** Clone for immutability (same as before) */
  public clone(): Tube {
    const bottomToTop = this.segments;
    return new Tube(bottomToTop.slice().reverse(), this.capacity);
  }

  /** Check if this tube is either empty or full+uniform */
  public isCompleteUniform(): boolean {
    if (this._stack.length === 0) return true;
    if (this._stack.length !== this.capacity) return false;
    const col = this.topColor()!;
    return this._stack.every(s => s.color === col);
  }
}
